import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetDoctorDashboardQuery } from '../../store/api/doctorApi';
import PageLoader from '../../components/PageLoader';
import { formatDateOnly } from '../../utils/helpers';

const DoctorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetDoctorDashboardQuery(undefined);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">Error loading dashboard</div>
      </div>
    );
  }

  const dashboardData = data?.data || {};
  const recentTickets = dashboardData.recent_tickets || [];

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Dashboard</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Dashboards</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-md-3 mb-md-5 mb-xl-10">
                <div className="card card-flush bgi-no-repeat bgi-size-contain bgi-position-x-end h-100 mb-5 mb-xl-10 bg-base">
                  <div className="card-header pt-5">
                    <div className="card-title d-flex flex-column">
                      <span className="fs-2hx fw-bold text-white me-2 lh-1 ls-n2">{dashboardData.new_tickets || 0}</span>
                      <span className="text-white opacity-75 pt-1 fw-semibold fs-4">New Tickets</span>
                    </div>
                  </div>
                  <div className="card-body d-flex align-items-end pt-0">
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-50">
                      <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill="white" fillOpacity="0.3" />
                      <path d="M20 3H10C9.4 3 9 3.4 9 4V6C9 6.6 9.4 7 10 7H20C20.6 7 21 6.6 21 6V4C21 3.4 20.6 3 20 3Z" fill="white" fillOpacity="0.5" />
                      <path d="M16 11V13C16 13.6 15.6 14 15 14H5C4.4 14 4 13.6 4 13V11C4 10.4 4.4 10 5 10H15C15.6 10 16 10.4 16 11Z" fill="white" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 mb-md-5 mb-xl-10">
                <div className="card card-flush bgi-no-repeat bgi-size-contain bgi-position-x-end h-100 mb-5 mb-xl-10 bg-base">
                  <div className="card-header pt-5">
                    <div className="card-title d-flex flex-column">
                      <span className="fs-2hx fw-bold text-white me-2 lh-1 ls-n2">{dashboardData.open_tickets || 0}</span>
                      <span className="text-white opacity-75 pt-1 fw-semibold fs-4">Tickets Opened</span>
                    </div>
                  </div>
                  <div className="card-body d-flex align-items-end pt-0">
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-50">
                      <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill="white" fillOpacity="0.3" />
                      <circle cx="12" cy="12" r="2" fill="white" />
                      <circle cx="8" cy="12" r="2" fill="white" />
                      <circle cx="16" cy="12" r="2" fill="white" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 mb-md-5 mb-xl-10">
                <div className="card card-flush bgi-no-repeat bgi-size-contain bgi-position-x-end h-100 mb-5 mb-xl-10 bg-base">
                  <div className="card-header pt-5">
                    <div className="card-title d-flex flex-column">
                      <span className="fs-2hx fw-bold text-white me-2 lh-1 ls-n2">{dashboardData.close_tickets || 0}</span>
                      <span className="text-white opacity-75 pt-1 fw-semibold fs-4">Closed Tickets</span>
                    </div>
                  </div>
                  <div className="card-body d-flex align-items-end pt-0">
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-50">
                      <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill="white" fillOpacity="0.3" />
                      <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="col-md-3 mb-md-5 mb-xl-10">
                <div className="card card-flush bgi-no-repeat bgi-size-contain bgi-position-x-end h-100 mb-5 mb-xl-10 bg-base">
                  <div className="card-header pt-5">
                    <div className="card-title d-flex flex-column">
                      <span className="fs-2hx fw-bold text-white me-2 lh-1 ls-n2">{dashboardData.total_tickets || 0}</span>
                      <span className="text-white opacity-75 pt-1 fw-semibold fs-4">Total Tickets</span>
                    </div>
                  </div>
                  <div className="card-body d-flex align-items-end pt-0">
                    <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-50">
                      <path d="M16 17V19C16 19.6 15.6 20 15 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill="white" fillOpacity="0.3" />
                      <path d="M12 17V19C12 19.6 11.6 20 11 20H5C4.4 20 4 19.6 4 19V7C4 6.4 4.4 6 5 6H9C9.6 6 10 6.4 10 7V9C10 9.6 10.4 10 11 10H15C15.6 10 16 10.4 16 11V17Z" fill="white" fillOpacity="0.5" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-12">
                <div className="card card-flush">
                  <div className="card-header pt-5">
                    <h3 className="card-title align-items-start flex-column">
                      <span className="card-label fw-bold text-dark">Recent Tickets</span>
                      <span className="text-gray-400 mt-1 fw-semibold fs-6">Latest tickets assigned to you</span>
                    </h3>
                  </div>
                  <div className="card-body pt-0">
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-5">
                        <thead>
                          <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>#</th>
                            <th>Ticket ID</th>
                            <th>User</th>
                            <th>Subject</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody className="fw-semibold text-gray-600">
                          {recentTickets.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center py-10">
                                <span className="text-muted">No recent tickets found</span>
                              </td>
                            </tr>
                          ) : (
                            recentTickets.map((ticket: any, index: number) => (
                              <tr key={ticket.id}>
                                <td>{index + 1}</td>
                                <td>#{ticket.id}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {ticket.user?.picture && (
                                      <img src={ticket.user.picture} alt={ticket.user?.name || 'User'} className="w-30px h-30px rounded-circle me-3" />
                                    )}
                                    <div>
                                      <div className="fw-bold">{ticket.user?.name || 'N/A'}</div>
                                      <div className="text-muted fs-7">{ticket.user?.email || ''}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>{ticket.subject || 'N/A'}</td>
                                <td>
                                  <span className={`badge badge-light-${ticket.status === 'Opened' ? 'success' : ticket.status === 'Closed' ? 'danger' : 'warning'}`}>
                                    {ticket.status || 'N/A'}
                                  </span>
                                </td>
                                <td>{formatDateOnly(ticket.created_at)}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => navigate(`/doctor/ticket/${ticket.id}`)}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
